<template lang="pug">
.segment-row
  .segment-selector-section
    .title-with-info
      h3 {{ `${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment')} ${index + 1}` }}
    .segment-selector
      aiq-select(
        :model-value="segment.segment_names"
        :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.select_segment')"
        :size="size"
        :disabled="disabled"
        multiple
        collapse-tags
        collapse-tags-tooltip
        clearable
        @update:model-value="onSegmentNamesChange"
        popper-class="pulse-config-dropdown pulse-segment-checkbox-dropdown"
      )
        aiq-option(
          v-for="seg in availableSegments"
          :key="seg"
          :label="seg"
          :value="seg"
        )
          .checkbox-option
            aiq-checkbox(
              :model-value="(segment.segment_names || []).includes(seg)"
              @click.stop
            )
            span.option-label {{ seg }}

  .segment-metrics-section
    .section-header
      .title-with-info
        h3 {{ `${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment')} ${index + 1} ${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment_metrics')}` }}
      .metric-count(v-if="hasSegmentsSelected") {{ (segment.metrics || []).length }} / {{ maxMetrics }}
    PulseMetricPicker(
      v-if="hasSegmentsSelected"
      :metrics="segment.metrics || []"
      :availableMetrics="availableMetrics"
      :maxMetrics="maxMetrics"
      :size="size"
      :disabled="disabled"
      @update="onMetricsUpdate"
    )
    .no-segment(v-else) {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.select_segment_to_add_metrics') }}
</template>

<script>
import PulseMetricPicker from './PulseMetricPicker.vue';
import { MAX_METRICS_PER_SECTION } from '@/pages/Pulse/365/constants';

export default {
  name: 'PulseConfigSegment',
  components: {
    PulseMetricPicker,
  },
  props: {
    segment: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    availableSegments: {
      type: Array,
      required: true,
    },
    availableMetrics: {
      type: Array,
      required: true,
    },
    maxMetrics: {
      type: Number,
      default: MAX_METRICS_PER_SECTION,
    },
    // '' keeps the Settings page on Element Plus's default size; the My Account
    // modal passes 'small' to match its other tabs.
    size: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  computed: {
    hasSegmentsSelected() {
      const segmentNames = this.segment.segment_names;
      return Array.isArray(segmentNames) && segmentNames.length > 0;
    },
  },
  methods: {
    emitUpdate(updates) {
      this.$emit('update', this.index, { ...this.segment, ...updates });
    },
    onSegmentNamesChange(newSegmentNames) {
      const hasSegments = Array.isArray(newSegmentNames) && newSegmentNames.length > 0;
      this.emitUpdate({
        segment_names: newSegmentNames,
        metrics: hasSegments ? (this.segment.metrics || []) : [],
      });
    },
    onMetricsUpdate(metrics) {
      this.emitUpdate({ metrics });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../../styles/aiq-variables.scss";

.segment-row {
  display: grid;
  grid-template-columns: 0.3fr 0.7fr;
  gap: 20px;
  padding-top: 20px;

  &:not(:last-of-type) {
    padding-bottom: 20px;
    border-bottom: 1px solid $aiq-bdc-input-default;
  }
}

.segment-selector-section {
  .title-with-info {
    margin-bottom: 15px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $aiq-color-text-primary;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

.segment-selector {
  width: 100%;

  ::v-deep {
    .el-select__tags {
      flex-wrap: wrap;
      gap: 4px;
    }

    .el-tag {
      margin: 2px 0;
    }
  }
}

.no-segment {
  color: $aiq-color-text-muted;
  font-size: 14px;
  padding: 10px 0;
}
</style>

<style lang="scss">
@import "../../../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .segment-row {
  &:not(:last-of-type) {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .metric-count {
    color: white;
  }

  .no-segment {
    color: white;
  }
}
</style>
